/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { ChangeEvent, useState, useRef, useEffect, useCallback } from 'react';
import SelectVariableModal, { ListItem } from 'src/components/modals/SelectVariableModal/SelectVariableModal';
import { useOutsideClick } from 'src/hooks/utils';
import { t } from 'i18next';

import Icon from '../../ui/Icon/Icon';
import Button, { ButtonToneProps } from '../../ui/Button/Button';

import styles from './TextAreaMenu.module.scss';

export interface TextAreaMenuProps {
  label?: string;
  value: string;
  onChange?: (e: ChangeEvent<HTMLDivElement>, cursorPosition?: number) => void;
  className?: string;
  variables?: ListItem[];
  disabled?: boolean;
  /**
   * When provided, picking an entry calls this instead of inserting it at the
   * cursor. Used by the workflow action editor where an entry is a template
   * whose body replaces the editor content.
   */
  onSelect?: (variableName: string) => void;
  /**
   * Label for the picker button and modal heading. Defaults to the classic
   * variable-picker wording; the workflow action editor overrides it because
   * its picker lists templates, not variables.
   */
  insertButtonTitle?: string;
}

const TextAreaMenu: React.FC<TextAreaMenuProps> = ({
  label,
  value,
  onChange,
  className,
  variables,
  disabled = false,
  onSelect,
  insertButtonTitle = t('general.insert_variable'),
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formatStates, setFormatStates] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const divRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (divRef.current) {
      const selection = window.getSelection();
      const range = document.createRange();
      divRef.current.innerHTML = value;

      if (selection && range) {
        range.selectNodeContents(divRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [value]);

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsModalOpen(false);
    }
  };

  // register click outside event to close the menu
  useOutsideClick(modalRef, handleClickOutside);

  const handleVariableInsertionClick = () => {
    setIsModalOpen(true);
  };

  const handleVariableSelect = (variableName: string) => {
    setIsModalOpen(false);
    if (onSelect) {
      onSelect(variableName);
      return;
    }
    if (divRef.current && onChange) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();

        const span = document.createElement('span');
        span.className = 'variableInput';
        span.textContent = variableName;
        const emptySpan = document.createElement('span');
        emptySpan.className = 'standardInput';
        emptySpan.textContent = '\u00A0';
        range.insertNode(emptySpan);
        range.insertNode(span);
        range.setStartAfter(emptySpan);
        range.setEndAfter(emptySpan);
        selection.removeAllRanges();
        selection.addRange(range);

        const newValue = divRef.current.innerHTML;
        onChange({ target: { innerHTML: newValue } } as unknown as ChangeEvent<HTMLDivElement>);
      }
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const cursorPosition = window.getSelection()?.getRangeAt(0).endOffset;
    if (onChange) {
      onChange(e as unknown as ChangeEvent<HTMLDivElement>, cursorPosition);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      if (range) {
        setTimeout(() => {
          const cursorPosition = range.endOffset;
          const newValue = divRef.current?.innerHTML;
          if (newValue && onChange) {
            onChange({
              target: { innerHTML: newValue },
              cursorPosition,
            } as unknown as ChangeEvent<HTMLDivElement>);
          }
        }, 0);
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (selection) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    updateFormatStates();
  };

  const updateFormatStates = useCallback(() => {
    setFormatStates({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    });
  }, []);

  const handleFormat = (command: string) => {
    document.execCommand(command);
    if (divRef.current && onChange) {
      const newValue = divRef.current.innerHTML;
      onChange({ target: { innerHTML: newValue } } as unknown as ChangeEvent<HTMLDivElement>);
    }
    updateFormatStates();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>, command: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFormat(command);
    }
  };

  return (
    <div className={styles.root}>
      {label && <p className="label typo-text-14">{label}</p>}
      <div className="relative textAreaMenuContent overflow-x-hidden">
        <div className="header flex justify-between items-end mb-8">
          <div className="gap-x-2 px-3.5 py-5 mt-8 flex justify-center items-center">
            <div
              role="button"
              aria-label="bold"
              tabIndex={0}
              className={formatStates.bold ? 'bg-color-light-yellow rounded' : ''}
              onClick={() => handleFormat('bold')}
              onKeyPress={e => handleKeyPress(e, 'bold')}>
              <Icon name="bold" width={20} height={20} />
            </div>
            <div
              role="button"
              aria-label="italic"
              tabIndex={0}
              className={formatStates.italic ? 'bg-color-light-yellow rounded' : ''}
              onClick={() => handleFormat('italic')}
              onKeyPress={e => handleKeyPress(e, 'italic')}>
              <Icon name="italic" width={20} height={20} />
            </div>
            <div
              role="button"
              aria-label="underline"
              tabIndex={0}
              className={formatStates.underline ? 'bg-color-light-yellow rounded' : ''}
              onClick={() => handleFormat('underline')}
              onKeyPress={e => handleKeyPress(e, 'underline')}>
              <Icon name="underline" width={20} height={20} />
            </div>
          </div>
          {variables && (
            <div className={`pb-2 pt-2 pr-2 ${isModalOpen ? 'modalOpen' : ''}`}>
              <Button
                icon="variable"
                title={insertButtonTitle}
                tone={ButtonToneProps.SECONDARY}
                onClick={handleVariableInsertionClick}
              />
            </div>
          )}
        </div>
        <div
          contentEditable={!disabled}
          onInput={!disabled ? handleInput : undefined}
          onKeyDown={!disabled ? handleKeyDown : undefined}
          onMouseUp={!disabled ? handleMouseUp : undefined}
          className={`${className} textAreaInput overflow-x-hidden overflow-y-auto border-subtle rounded-base p-12 typo-text-14 ${disabled ? 'opacity-70 cursor-not-allowed surface-disabled' : ''}`}
          ref={divRef}
          id="textAreaInput"
          role="presentation"
        />
      </div>
      {isModalOpen && (
        <div ref={modalRef} className="absolute left-[27.5rem] bottom-[19.1rem]">
          <SelectVariableModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            listItems={variables || []}
            onItemSelect={handleVariableSelect}
            title={insertButtonTitle}
          />
        </div>
      )}
    </div>
  );
};

export default TextAreaMenu;
